import React, { useCallback, useEffect, useState } from "react";
import { Button } from "../common/Button";
import { useStepData } from "../../hooks/useStepData";
import { useFunnelNavigation } from "../../hooks/useFunnelNavigation";
import { InfoBox } from "../common/InfoBox";
import { InputField } from "../common/InputField";
import { useFunnel } from "../../context/FunnelContext";
import { StepHeader } from "../common/StepHeader";
import { useMeasurementSystem } from "../../hooks/useMeasurementSystem";
import { MeasurementToggle } from "../common/MeasurementToggle";

const WEIGHT_LIMITS = {
  imperial: {
    lbs: { min: 80, max: 500 },
  },
  metric: {
    kg: { min: 36, max: 227 },
  },
};

const BMI_CATEGORIES = {
  UNDERWEIGHT: {
    max: 18.5,
    color: "warning",
    message:
      "Given your current height-to-weight ratio, we do not recommend intermittent fasting.",
  },
  NORMAL: {
    max: 24.9,
    color: "success",
    message:
      "You're starting from a great place! Now we'll use your BMI to create a program tailored to your needs.",
  },
  OVERWEIGHT: {
    max: 29.9,
    color: "warning",
    message:
      "You have some work ahead of you, but it's great that you're taking this first step. We'll use your BMI to create a weight loss program just for you.",
  },
  OBESE: {
    max: Infinity,
    color: "error",
    message: "Please consult with a healthcare professional.",
  },
};

export function WeightInput({ config }) {
  const { measurementSystem } = useMeasurementSystem();
  const { data, updateStepData } = useStepData(config.field);
  const { state } = useFunnel();
  const { nextStep } = useFunnelNavigation();

  const [weight, setWeight] = useState(data?.weight || "");
  const [unit, setUnit] = useState(measurementSystem);
  const [bmi, setBmi] = useState(null);
  const [bmiCategory, setBmiCategory] = useState(null);

  const calculateBmi = useCallback(() => {
    const heightData = state.userData.height;
    if (!heightData || !weight) return null;

    let weightInKg =
      unit === "imperial" ? Number(weight) * 0.453592 : Number(weight);
    let heightInM;

    if (heightData.unit === "imperial") {
      const totalInches =
        Number(heightData.feet) * 12 + Number(heightData.inches);
      heightInM = totalInches * 0.0254;
    } else {
      heightInM = Number(heightData.cm) / 100;
    }

    const calculatedBmi = weightInKg / (heightInM * heightInM);
    return Math.round(calculatedBmi * 10) / 10;
  }, [weight, unit, state.userData.height]);

  const getWeightErrorMessage = () => {
    if (!weight) return "";

    const limits = WEIGHT_LIMITS[unit][unit === "imperial" ? "lbs" : "kg"];
    const weightNum = Number(weight);

    if (weightNum < limits.min) {
      return `Please enter a weight of at least ${limits.min} ${unit === "imperial" ? "lbs" : "kg"}`;
    }
    if (weightNum > limits.max) {
      return `Please enter a weight less than ${limits.max} ${unit === "imperial" ? "lbs" : "kg"}`;
    }
    return "";
  };

  const getBmiCategory = (bmiValue) => {
    if (!bmiValue) return null;
    if (bmiValue <= BMI_CATEGORIES.UNDERWEIGHT.max)
      return BMI_CATEGORIES.UNDERWEIGHT;
    if (bmiValue <= BMI_CATEGORIES.NORMAL.max) return BMI_CATEGORIES.NORMAL;
    if (bmiValue <= BMI_CATEGORIES.OVERWEIGHT.max)
      return BMI_CATEGORIES.OVERWEIGHT;
    return BMI_CATEGORIES.OBESE;
  };

  useEffect(() => {
    const calculatedBmi = calculateBmi();
    setBmi(calculatedBmi);
    setBmiCategory(getBmiCategory(calculatedBmi));
  }, [calculateBmi]);

  const handleWeightChange = (value) => {
    setWeight(value);
  };

  const isValidWeight = () => {
    const numWeight = Number(weight);
    const limits = WEIGHT_LIMITS[unit][unit === "imperial" ? "lbs" : "kg"];
    return numWeight >= limits.min && numWeight <= limits.max;
  };

  const handleNext = () => {
    if (isValidWeight()) {
      updateStepData({ weight, unit, bmi });
      nextStep();
    }
  };

  return (
    <div className="flex flex-col px-4">
      <div className="space-y-4">
        <StepHeader
          title={config.title}
          subtitle={config.subtitle}
          order={config.headerOrder}
        />

        <MeasurementToggle onChange={setUnit} />

        <div className="relative">
          <InputField
            value={weight}
            onChange={handleWeightChange}
            placeholder={unit === "imperial" ? "165" : "75"}
            unit={unit === "imperial" ? "lbs" : "kg"}
            type="number"
            min={WEIGHT_LIMITS[unit][unit === "imperial" ? "lbs" : "kg"].min}
            max={WEIGHT_LIMITS[unit][unit === "imperial" ? "lbs" : "kg"].max}
            error={getWeightErrorMessage()}
          />
        </div>

        {bmi && bmiCategory && isValidWeight() && (
          <InfoBox
            icon="info"
            variant={bmiCategory.color}
            title={`Your BMI: ${bmi}`}
            text={bmiCategory.message}
          />
        )}

        {weight && isValidWeight() && (
          <Button
            onClick={handleNext}
            className="w-full"
            disabled={!isValidWeight()}
          >
            Next
          </Button>
        )}
      </div>
    </div>
  );
}
